import React from 'react';
import {
  View,
  Flex,
  Text,
  Alert,
  IconCheckMarkSolid,
  IconCheckMarkIndeterminateLine,
  IconXLine,
  IconCommentsOnLine,
} from '@smarterservices/smarter-ui';
import { Heading, Button } from '@instructure/ui';
import { useState, useEffect, useRef } from 'react';
import parse from 'html-react-parser';
import CheckinItemReason from './Reason';
import ItemCriteria from './ItemCriteria';
import { getStore } from '../../../stores/StoreContext';
/**
 * @function CheckinItem
 *
 * @description Component for rendering and managing individual check-in items.
 *
 * @param {Object} item - The check-in item being rendered.
 * @param {Function} getNextItem - Function to get the next item in the list.
 * @param {Function} onComplete - Function to call when checkin is complete.
 *
 * @returns {ReactElement} - The rendered check-in item.
 */
const CheckinItem = ({ item, getNextItem, onComplete }) => {
  const CheckinStore = getStore('checkinStore');
  const AppStore = getStore('appStore');

  const [showComments, setShowComments] = useState(false);
  const [showSaveButtonBar, setShowSaveButtonBar] = useState(false);
  const [changeToStatus, setChangeToStatus] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [buttonText, setButtonText] = useState('Save');
  const [options, setOptions] = useState([]);
  const [showRejectQuestion, setShowRejectQuestion] = useState(false);

  const videoRef = useRef(null);
  const statusOptions = CheckinStore.statusOptions;

  const handleStartHls = () => {
    const hls = videoRef.current?.getHlsInstance();
    if (hls) {
      // This resumes loading segments if stopped
      //hls.startLoad();
      console.log('HLS loading resumed');
    }
  };

  const handleStopHls = () => {
    const hls = videoRef.current?.getHlsInstance();
    if (hls) {
      // This stops loading new segments
      //hls.stopLoad();
      console.log('HLS loading stopped');
    }
  };

  const handleOnStatusChange = (value) => {
    setChangeToStatus(value);
    if (value !== 'accept' && CheckinStore.activeItem.commentsRequired) {
      setShowComments(true);
    } else {
      setShowComments(false);
    }
  };

  const handleSave = async (nextAction) => {
    const data = { status: selectedOption };
    const activeItem = CheckinStore.activeItem;
    setSelectedOption(null);

    await useCheckinStore
      .getState()
      .updateItem(CheckinStore.activeItem.id, data);

    if (nextAction === 'exit') {
      AppStore.setActiveCheckinItem(null);
    } else {
      const nextItem = getNextItem(CheckinStore.activeItem);

      if (nextItem) {
        CheckinStore.setActiveItem(nextItem);
        setActiveItem(nextItem);
      } else {
        // If there is no next item call the onComplete function.
        if (onComplete) {
          onComplete();
        }
      }
    }
  };

  useEffect(() => {
    const runUpdate = async () => {
      if (selectedOption === statusOptions.ACCEPT.value) {
        await handleSave('next');
        setShowSaveButtonBar(false);
      } else if (selectedOption === statusOptions.ACCEPT_COMMENT.value) {
        setButtonText('Accept');
        setShowSaveButtonBar(true);
      } else if (selectedOption === statusOptions.REJECT.value) {
        setButtonText('Reject');
        setShowSaveButtonBar(true);
      } else if (selectedOption === statusOptions.OPTOUT.value) {
        setButtonText('Opt-out');
        setShowSaveButtonBar(true);
      } else {
      }
    };

    runUpdate();
  }, [selectedOption]);

  useEffect(() => {
    setActiveItem(CheckinStore.activeItem);

    setSelectedOption(CheckinStore.activeItem?.status);
  }, [CheckinStore.activeItem]);

  return (
    <>
      {activeItem && (
        <>
          <Flex direction="row" margin="none none small none" gap="x-small">
            <Flex.Item>
              <Heading>{activeItem.title}</Heading>
            </Flex.Item>
            <Flex.Item>
              <ItemCriteria criteria={activeItem.criteria} />
            </Flex.Item>
          </Flex>

          <View as="div" margin="none none small none" textAlign="center">
            {/**
            <HlsVideoPlayer
              ref={videoRef}
              url="https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8"
              startTime={55}
              endTime={60}
              onEndTime={() => console.log('Reached the end time')}
              hlsConfig={{ autoStartLoad: true }}
              videoProps={{ controls: true }}
            />
             */}
          </View>

          {activeItem?.information && (
            <Alert variant="info" margin="none">
              <Text as="div" size="medium" weight="bold">
                Specific Notes:
              </Text>
              <Text as="div">{parse(activeItem.information)}</Text>
            </Alert>
          )}

          <Flex
            direction="row"
            justifyItems="space-between"
            margin="small none"
            gap="x-small"
          >
            <Flex.Item width="200px" shouldShrink>
              <Button
                renderIcon={IconXLine}
                color="danger"
                withBackground={selectedOption === 'reject' ? true : false}
                display="block"
                size="small"
                onClick={() => setSelectedOption(statusOptions.REJECT.value)}
              >
                Reject
              </Button>
            </Flex.Item>
            {!activeItem.required && (
              <Flex.Item width="200px" shouldShrink>
                <Button
                  renderIcon={IconCheckMarkIndeterminateLine}
                  color="primary"
                  withBackground={selectedOption === 'optout' ? true : false}
                  display="block"
                  size="small"
                  onClick={() => setSelectedOption(statusOptions.OPTOUT.value)}
                >
                  Opt-Out
                </Button>
              </Flex.Item>
            )}

            <Flex.Item width="200px" shouldShrink>
              <Button
                renderIcon={IconCommentsOnLine}
                color="success"
                withBackground={
                  selectedOption === 'accept-with-comment' ? true : false
                }
                display="block"
                size="small"
                onClick={() =>
                  setSelectedOption(statusOptions.ACCEPT_COMMENT.value)
                }
              >
                Accept w/ Comment
              </Button>
            </Flex.Item>

            <Flex.Item width="200px" shouldShrink>
              <Button
                renderIcon={IconCheckMarkSolid}
                color="success"
                withBackground={selectedOption === 'accept' ? true : false}
                display="block"
                size="small"
                onClick={() => setSelectedOption(statusOptions.ACCEPT.value)}
              >
                Accept
              </Button>
            </Flex.Item>
          </Flex>

          <CheckinItemReason
            reasons={activeItem.reasons}
            status={selectedOption}
          />
          {showSaveButtonBar === true && (
            <Flex
              direction="row"
              justifyItems="space-between"
              margin="small none none none"
              gap="x-small"
            >
              <Flex.Item>
                <Button color="primary" onClick={() => handleSave('exit')}>
                  {buttonText} & Exit
                </Button>
              </Flex.Item>

              <Flex.Item>
                <Button color="primary" onClick={() => handleSave('next')}>
                  {buttonText} & Next
                </Button>
              </Flex.Item>
            </Flex>
          )}
        </>
      )}
    </>
  );
};

export default CheckinItem;
